// What the Trip Name typeahead offers, and how a page hands it over.
//
// The candidates are the trips that already exist beside the record being
// edited -- an item's legs on the trip page, a logbook's entries on the entry
// page -- so this is where those two very different reads are turned into the
// one row shape `TripSuggestInput` renders:
//
//   { key, tripGroupId, name, startDate, endDate, throughDate, count }
//
// `startDate`/`endDate`/`throughDate` are there so `tripGroupDateRange` can
// date the row without a second shape: dating a row is not decoration here, it
// is how the user tells two trips called "Day" apart.
//
// Only trips with an IDENTITY are offered. A legacy name-keyed trip is
// deliberately not a row: picking one could only write the name, which is what
// typing already does, so the row would claim to bind and quietly not. After
// the backfill there are no such trips left; before it, they still show up in
// the list and on their own pages exactly as they always have.

import { groupTrips, isTripGroupIdKey, tripGroupIdFromKey } from '../fleet-shared/TripGroups.mjs';
import { entryTripKey, tripNameOf, summarizeTripEntries } from '../fleet-shared/LogbookTrips.mjs';
import { sortTripsAscending } from '../fleet-shared/TripFields.mjs';

// The identity fields a suggestion row needs, off a built trip group.
function suggestionFromGroup(group) {
  return {
    key: group.key,
    tripGroupId: group.tripGroupId,
    name: group.name,
    startDate: group.startDate,
    endDate: group.endDate,
    throughDate: group.throughDate,
    count: group.legs.length,
  };
}

// The item's trips, newest first, from its legs.
export function tripSuggestionsFromLegs(legs) {
  return groupTrips(legs || [])
    .filter((group) => isTripGroupIdKey(group.key))
    .map(suggestionFromGroup)
    .sort((a, b) => Date.parse(b.startDate ?? 0) - Date.parse(a.startDate ?? 0));
}

// The logbook's trips, newest first, from its entries. Grouped by the same
// entryTripKey the per-trip and per-year pages use, so a row here and a row
// there are the same trip by construction.
export function tripSuggestionsFromEntries(entries, typeId) {
  const rows = new Map();
  for (const entry of entries || []) {
    const key = entryTripKey(entry, typeId);
    if (!isTripGroupIdKey(key)) continue;
    if (!rows.has(key)) rows.set(key, []);
    rows.get(key).push(entry);
  }
  return [...rows.entries()]
    .map(([key, rowEntries]) => {
      const summary = summarizeTripEntries(rowEntries, typeId);
      // Oldest-first, so the name below is the spelling the trip was FIRST
      // given. Ordered exactly as tripsInYear orders a row -- reversed, then
      // sorted -- rather than read off the arrival order: these entries come
      // back newest-first (listCompletedTasksForLogbook queries DESC for the
      // logbook LIST's sake), so `rowEntries[0]` is the LATEST spelling. A trip
      // typed "Day" and later "day" would then be labelled one way here and the
      // other on the year page, which is the disagreement both surfaces
      // grouping on the same key exists to prevent.
      const ordered = sortTripsAscending([...rowEntries].reverse());
      return {
        key,
        tripGroupId: tripGroupIdFromKey(key),
        name: tripNameOf(ordered[0], typeId).trim(),
        startDate: summary.startDate,
        endDate: summary.endDate,
        count: summary.count,
      };
    })
    .sort((a, b) => Date.parse(b.startDate ?? 0) - Date.parse(a.startDate ?? 0));
}

// Clone `fields`, giving every trip-binding field its candidate loader.
//
// A clone rather than a mutation because the registries are module-level and
// shared by every page in the app -- the same reason buildEntryFields and
// buildTaskFields clone before injecting a counter's `selects`. A field with no
// loader renders as a plain text box, which is what this field was before and
// what every caller that has no trips to offer should get.
export function withTripSuggestions(fields, loadSuggestions) {
  if (!loadSuggestions) return fields;
  return (fields || []).map((field) =>
    field.type === 'trip' ? { ...field, loadSuggestions } : field
  );
}
